import React from 'react';

import { Link, LegacyText, ArrowForwardIcon } from '@gogame/components';
import { Route, Link as RouterLink } from '@gogame/screens';
import { Sentry } from '@gogame/components';
import { useScreenState } from '@gogame/screens';

import {
  BreadcrumbsContext,
  BreadcrumbsNode,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Breadcrumbs';
import BudgetSum from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/BudgetSum';
import { GroupLevelProvider } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Group';
import Guard from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Guard';

import { BreadcrumbsStyled, PaperLevelsStyled, Header } from './components';

const SubLevelProvider = ({
  children,
  withBudgetSum = true,
  widthBudgetSum,
}) => {
  const { items, path } = React.useContext(BreadcrumbsContext);
  const { version } = useScreenState();

  return (
    <Guard path={path}>
      <GroupLevelProvider levelPath={path}>
        <Header gutter={8} align={'center'}>
          {withBudgetSum ? <BudgetSum minWidthValue={widthBudgetSum} /> : null}
          <BreadcrumbsStyled
            separator={
              <ArrowForwardIcon sx={{ fontSize: 16 }} color={'action'} />
            }
          >
            {items.map(({ label, to }, i) =>
              items.length - 1 !== i ? (
                <Link
                  component={RouterLink}
                  key={`breadcrumbs_${i}`}
                  to={version ? { ...to, search: `?version=${version}` } : to}
                  label={`breadcrumb_${label}_${i}`}
                >
                  {label}
                </Link>
              ) : (
                <LegacyText
                  key={`breadcrumbs_${i}`}
                  label={`breadcrumb_${label}_${i}`}
                >
                  {label}
                </LegacyText>
              )
            )}
          </BreadcrumbsStyled>
        </Header>
        {children}
      </GroupLevelProvider>
    </Guard>
  );
};

const Levels = ({
  affiliateLevel,
  platformLevel,
  channelLevel,
  applicationLevel,
  networkLevel,
  totalLevel,
  withBudgetSum,
  widthBudgetSum,
  countryLevel,
  managerLevel,
}) => (
  <PaperLevelsStyled variant={'outlined'} data-cy={'Levels'}>
    <Sentry.Routes>
      <Route path={'/'} element={<BreadcrumbsNode />}>
        <Route path={':network'} element={<BreadcrumbsNode />}>
          <Route path={':application'} element={<BreadcrumbsNode />}>
            <Route path={':channel'} element={<BreadcrumbsNode />}>
              <Route path={':platform'} element={<BreadcrumbsNode />}>
                <Route path={':affiliate'} element={<BreadcrumbsNode />}>
                  <Route path={':country'} element={<BreadcrumbsNode />}>
                    <Route path={':manager'} element={<BreadcrumbsNode />}>
                      <Route
                        index
                        element={
                          <SubLevelProvider
                            withBudgetSum={withBudgetSum}
                            widthBudgetSum={widthBudgetSum}
                          >
                            {managerLevel}
                          </SubLevelProvider>
                        }
                      />
                    </Route>

                    <Route
                      index
                      element={
                        <SubLevelProvider
                          withBudgetSum={withBudgetSum}
                          widthBudgetSum={widthBudgetSum}
                        >
                          {countryLevel}
                        </SubLevelProvider>
                      }
                    />
                  </Route>
                  <Route
                    index
                    element={
                      <SubLevelProvider
                        withBudgetSum={withBudgetSum}
                        widthBudgetSum={widthBudgetSum}
                      >
                        {affiliateLevel}
                      </SubLevelProvider>
                    }
                  />
                </Route>

                <Route
                  index
                  element={
                    <SubLevelProvider
                      withBudgetSum={withBudgetSum}
                      widthBudgetSum={widthBudgetSum}
                    >
                      {platformLevel}
                    </SubLevelProvider>
                  }
                />
              </Route>
              <Route
                index
                element={
                  <SubLevelProvider
                    withBudgetSum={withBudgetSum}
                    widthBudgetSum={widthBudgetSum}
                  >
                    {channelLevel}
                  </SubLevelProvider>
                }
              />
            </Route>
            <Route
              index
              element={
                <SubLevelProvider
                  withBudgetSum={withBudgetSum}
                  widthBudgetSum={widthBudgetSum}
                >
                  {applicationLevel}
                </SubLevelProvider>
              }
            />
          </Route>
          <Route
            index
            element={
              <SubLevelProvider
                withBudgetSum={withBudgetSum}
                widthBudgetSum={widthBudgetSum}
              >
                {networkLevel}
              </SubLevelProvider>
            }
          />
        </Route>
        <Route index element={totalLevel} />
      </Route>
    </Sentry.Routes>
  </PaperLevelsStyled>
);

export default Levels;
